import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useShallow } from 'zustand/react/shallow';

import { Dropdown, DropdownOption, Icon, Input } from '@column/column-ui-kit';

import { useAddress } from '~/hooks/useAddress';
import { useDebounce } from '~/hooks/useDebounce';
import { AddressType } from '~/repositories';
import { LibraryStatus, useGoogleMapsStore } from '~/stores/GoogleMaps';
import { useNotificationStore } from '~/stores/Notification';

interface AddressProps {
  onChange?: (address: AddressType) => void;
  justUS?: boolean;
  isDisabled?: boolean;
  value?: string;
  onValueChange?: (value: string) => void;
}

const StyledDropdown = styled(Dropdown)`
  --dropdown-custom-padding: 0;
`;

export const AddressField: React.FC<AddressProps> = (props) => {
  const { states } = useAddress({ types: ['state'] });
  const addDangerNotifcation = useNotificationStore((s) => s.addDangerNotification);
  const { loadLibrary, getSessionToken, clearSessionToken, isMapsLoaded } = useGoogleMapsStore(
    useShallow((s) => ({
      loadLibrary: s.loadLibrary,
      getSessionToken: s.getSessionToken,
      clearSessionToken: s.clearSessionToken,
      isMapsLoaded: s.libraryStatus.places === LibraryStatus.Loaded,
    }))
  );
  const [options, setOptions] = useState<DropdownOption[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [internalValue, setInternalValue] = useState('');
  const requestIdRef = useRef(0);

  useEffect(() => {
    loadLibrary(['places']).catch(() => {
      addDangerNotifcation({ content: 'Failed to load map autocomplete' });
    });
  }, [loadLibrary, addDangerNotifcation]);

  const inputValue = props.value !== undefined ? props.value : internalValue;

  const handleSetValue = (val: string) => {
    if (props.onValueChange) props.onValueChange(val);
    else setInternalValue(val);
  };

  const handleSelect = async (prediction: google.maps.places.PlacePrediction) => {
    requestIdRef.current++;
    setOptions([]);
    setOpen(false);

    try {
      const place = prediction.toPlace();
      await place.fetchFields({ fields: ['addressComponents'] });
      clearSessionToken();

      if (!props.onChange || !place.addressComponents) {
        return;
      }

      const components = place.addressComponents;
      const findComponent = (type: string) => components.find((c) => c.types[0] === type);
      const stateComponent = findComponent('administrative_area_level_1');
      const stateCode = stateComponent
        ? parseStateDetails(
            { long_name: stateComponent.longText ?? '', short_name: stateComponent.shortText ?? '' },
            states
          )?.stateCode
        : '';

      const address: AddressType = {
        line1: `${findComponent('street_number')?.longText ?? ''} ${findComponent('route')?.longText ?? ''}`.trim(),
        city: findComponent('locality')?.longText ?? '',
        state: stateCode,
        countryCode: findComponent('country')?.shortText ?? '',
        postalCode: findComponent('postal_code')?.longText ?? '',
      };
      props.onChange(address);
    } catch (e) {
      console.error('AddressField', e);
    }
  };

  const fetchAutocompleteSuggestions = useDebounce(
    async (input: string) => {
      const request: google.maps.places.AutocompleteRequest = {
        input,
        sessionToken: getSessionToken(),
      };
      if (props.justUS) {
        request.includedRegionCodes = ['us'];
      }
      const currentRequestId = ++requestIdRef.current;
      try {
        const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
        if (currentRequestId !== requestIdRef.current) {
          return;
        }
        const newOptions: DropdownOption[] = suggestions
          .map((suggestion) => suggestion.placePrediction)
          .filter((prediction): prediction is google.maps.places.PlacePrediction => prediction !== null)
          .map((prediction) => ({
            label: `${prediction.mainText?.text ?? prediction.text.text} (${prediction.secondaryText?.text ?? ''})`,
            onClick: () => handleSelect(prediction),
          }));
        setOptions(newOptions);
        if (newOptions.length > 0) {
          setOpen(true);
        }
      } catch (e) {
        console.error('AddressField', e);
      }
    },
    300,
    [props.justUS, getSessionToken]
  );

  useEffect(() => {
    if (!isMapsLoaded || !inputValue) {
      requestIdRef.current++;
      setOptions([]);
      return;
    }
    fetchAutocompleteSuggestions(inputValue);
  }, [inputValue, isMapsLoaded, fetchAutocompleteSuggestions]);

  return (
    <StyledDropdown
      isOpen={open}
      options={options}
      fullWidth
      customLabel={
        <Input
          value={inputValue}
          onChange={handleSetValue}
          isDisabled={!isMapsLoaded || props.isDisabled}
          placeholder="Search for address"
          icon={<Icon.Search />}
          onFocus={() => options.length > 0 && setOpen(true)}
          onBlur={() => setOpen(false)}
        />
      }
    />
  );
};

const parseStateDetails = (
  stateDetails: { long_name: string; short_name: string },
  allStates: State[]
): { stateCode?: string } => {
  if (!stateDetails || !stateDetails.short_name) {
    return {};
  }

  const exactMatch = allStates.find(
    (s) => s.state_code === stateDetails.short_name || s.name === stateDetails.long_name
  );
  return { stateCode: exactMatch?.state_code };
};
